import { ProductId } from "./constants";
import {
  FeatureToggleDto,
  OesAccountDto,
  ScopesDto,
  SignupStatusDto,
  UserAccountDto,
  UsernameDto,
  UserPackageDto,
} from "./dtos";
import { HttpClient } from "./http";

export interface UserTraits {
  isVerified?: boolean;
  featureToggles?: string;
  accountType?: string;
  isResellerCustomer?: boolean;
  userId?: number;
  package?: string;
  hasTneAccess?: boolean;
  eiPlan?: string;
  mcPlan?: string;
  legacyMcPlan?: string;
  legacyMCDiscountEligible?: boolean;
  legacyMCDiscountActive?: boolean;
  legacyMCDiscountEndDate?: Date;
  legacyMCDiscountEndDateFormatted?: Date;
}

export class UserTraitsService {
  private http: HttpClient;

  constructor(apiHost: string, token: string) {
    this.http = new HttpClient(apiHost, token);
  }

  // --- API methods ---
  private async getUserAccount(): Promise<UserAccountDto> {
    const response = await this.http.get("user/account");
    if (response.ok) return (await response.json()) as UserAccountDto;
    throw new Error("Failed to load user profile");
  }

  private async getUsername(): Promise<UsernameDto> {
    const response = await this.http.get("user/username");
    if (response.ok) return (await response.json()) as UsernameDto;
    throw new Error("Failed to load username");
  }

  private async getFeatureToggles(): Promise<FeatureToggleDto[]> {
    const response = await this.http.get("feature_toggles");
    if (response.ok) return (await response.json()) as FeatureToggleDto[];
    throw new Error("Failed to load feature toggles");
  }

  private async getSignupStatus(): Promise<SignupStatusDto> {
    const response = await this.http.get("s2s/signup/status");
    if (response.ok) return (await response.json()) as SignupStatusDto;
    throw new Error("Failed to load sign up status");
  }

  private async getUserPackage(): Promise<UserPackageDto> {
    const response = await this.http.get("user/package");
    if (response.ok) return (await response.json()) as UserPackageDto;
    throw new Error("Failed to load user package");
  }

  private async getScopes(): Promise<ScopesDto> {
    const response = await this.http.get("scopes");
    if (response.ok) return (await response.json()) as ScopesDto;
    throw new Error("Failed to load scopes");
  }

  private async getOesAccount(): Promise<OesAccountDto> {
    const response = await this.http.get("accounts/offerings?_show&_pricing&_offerings");
    if (response.ok) return (await response.json()) as OesAccountDto;
    throw new Error("Failed to load OES account");
  }

  // --- Mapping helpers ---
  private isAccountVerified(signupStatus?: SignupStatusDto) {
    if (!signupStatus) return true;
    const {
      email_complete,
      email_required,
      mfa_complete,
      mfa_required,
      whitelabel_complete,
      whitelabel_required,
    } = signupStatus;
    if (email_required && !email_complete) return false;
    if (mfa_required && !mfa_complete) return false;
    if (whitelabel_required && !whitelabel_complete) return false;
    return true;
  }

  private mapScopesToTraits(scopes?: ScopesDto) {
    if (!scopes) return { hasTneAccess: false };
    const hasTneAccess =
      (scopes && scopes.scopes && scopes.scopes.includes("marketing.read")) || false;
    return { hasTneAccess };
  }

  private mapSignupStatusToTraits(signupStatus?: SignupStatusDto) {
    return { isVerified: this.isAccountVerified(signupStatus) };
  }

  private mapOesAccountToTraits(oesAccountDto?: OesAccountDto) {
    const traits: any = {
      eiPlan: null,
      mcPlan: null,
      legacyMcPlan: null,
      legacyMCDiscountEligible: false,
      legacyMCDiscountActive: false,
    };
    if (!oesAccountDto) return traits;
    const { offerings, entitlements } = oesAccountDto;
    const eiPlanName = /^sg\.ei\..+\.v\d+$/i;
    const mcPlanName = /^sg\.mc\..+\.v\d+$/i;
    offerings.forEach((offering: any) => {
      if (offering.name === ProductId.SG_MC_CLASSIC_TRIAL_DISCOUNT_V1) {
        const endDate = new Date(offering.end_date);
        const formattedEndDate = endDate.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        traits.legacyMCDiscountActive = true;
        traits.legacyMCDiscountEndDate = offering.end_date;
        traits.legacyMCDiscountEndDateFormatted = formattedEndDate;
        return;
      }
      if (offering.name === ProductId.SG_EI_MC_CLASSIC_ADDON_V1) {
        traits.legacyMcPlan = offering.name;
        return;
      }
      if (eiPlanName.test(offering.name) && offering.type === "package") {
        traits.eiPlan = offering.name;
        return;
      }
      if (mcPlanName.test(offering.name) && offering.type === "package") {
        traits.mcPlan = offering.name;
        return;
      }
    });
    traits.legacyMCDiscountEligible = entitlements.compat_mc_classic_trial_eligible || false;
    return traits;
  }

  private mapAccountToTraits(account?: UserAccountDto) {
    if (!account) return {};
    return {
      accountType: account.type,
      isResellerCustomer: account.is_reseller_customer,
    };
  }

  private mapUsernameToTraits(username?: UsernameDto) {
    if (!username) return {};
    return { userId: username.user_id };
  }

  private mapPackageToTraits(userPackage?: UserPackageDto) {
    if (!userPackage) return {};
    return { package: userPackage.name };
  }

  private mapFeatureTogglesToTraits(featureToggles?: FeatureToggleDto[]) {
    if (!featureToggles) return {};
    const featureTogglesCsv = featureToggles
      .map((ft) => `${ft.app_name}-${ft.feature_name}`)
      .join(",");
    return { featureToggles: featureTogglesCsv };
  }

  // --- Main fetchUserTraits method ---
  async fetch(): Promise<UserTraits> {
    const [username, account, featureToggles, signupStatus, userPackage, scopes, oesAccount] =
      await Promise.all([
        this.getUsername().catch(() => undefined),
        this.getUserAccount().catch(() => undefined),
        this.getFeatureToggles().catch(() => undefined),
        this.getSignupStatus().catch(() => undefined),
        this.getUserPackage().catch(() => undefined),
        this.getScopes().catch(() => undefined),
        this.getOesAccount().catch(() => undefined),
      ]);
    return {
      ...this.mapFeatureTogglesToTraits(featureToggles),
      ...this.mapPackageToTraits(userPackage),
      ...this.mapUsernameToTraits(username),
      ...this.mapAccountToTraits(account),
      ...this.mapSignupStatusToTraits(signupStatus),
      ...this.mapScopesToTraits(scopes),
      ...this.mapOesAccountToTraits(oesAccount),
    } as UserTraits;
  }
}
